import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  FaDesktop, 
  FaCogs, 
  FaDatabase, 
  FaStream, 
  FaTools,
  FaGlobe,
  FaMobile,
  FaServer,
  FaShieldAlt,
  FaChartLine
} from 'react-icons/fa';
import bgImage from '../assets/mobapp.jpg';
import ContactForm from '../components/ContactForm';

const CustomSoftwarePage = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });

  const webDevFeatures = [
    {
      icon: <FaGlobe />,
      title: 'Responsive Web Development',
      description: 'Build modern, mobile-first websites that adapt seamlessly to all devices and screen sizes using React, Next.js, and Tailwind CSS.',
      color: 'linear-gradient(135deg, #3b82f6, #2563eb)'
    },
    {
      icon: <FaDesktop />,
      title: 'Enterprise Web Applications',
      description: 'Custom web apps with robust architectures using Node.js, Express, and modern frontend frameworks for complex business needs.',
      color: 'linear-gradient(135deg, #8b5cf6, #7c3aed)'
    },
    {
      icon: <FaMobile />,
      title: 'Progressive Web Apps',
      description: 'Transform your website into an app-like experience with offline capabilities, push notifications, and home screen installation.',
      color: 'linear-gradient(135deg, #ec4899, #db2777)'
    },
    {
      icon: <FaServer />,
      title: 'Full-Stack Solutions',
      description: 'End-to-end development with MongoDB/PostgreSQL databases, REST/GraphQL APIs, and scalable cloud infrastructure.',
      color: 'linear-gradient(135deg, #10b981, #059669)'
    },
    {
      icon: <FaShieldAlt />,
      title: 'Web Security',
      description: 'Enterprise-grade security including OAuth, JWT, CSP, and regular penetration testing to protect your applications.',
      color: 'linear-gradient(135deg, #ef4444, #dc2626)'
    },
    {
      icon: <FaChartLine />,
      title: 'Web Analytics Integration',
      description: 'Implement advanced tracking with Google Analytics, Hotjar, and custom event monitoring for data-driven decisions.',
      color: 'linear-gradient(135deg, #f59e0b, #d97706)'
    }
  ];

  const customSoftwareFeatures = [
    {
      icon: <FaDesktop />,
      title: 'Tailored Desktop Applications',
      description: 'Cross-platform desktop apps built with Electron, .NET MAUI, or Flutter for Windows, macOS, and Linux environments.',
      color: 'linear-gradient(135deg, #6366f1, #4f46e5)'
    },
    {
      icon: <FaCogs />,
      title: 'Workflow Automation',
      description: 'Custom automation solutions that integrate with your existing tools to eliminate manual processes and reduce errors.',
      color: 'linear-gradient(135deg, #06b6d4, #0891b2)'
    },
    {
      icon: <FaDatabase />,
      title: 'Database Systems',
      description: 'Design and optimize SQL/NoSQL databases with efficient data models, indexing strategies, and query optimization.',
      color: 'linear-gradient(135deg, #14b8a6, #0d9488)'
    },
    {
      icon: <FaStream />,
      title: 'Real-Time Systems',
      description: 'WebSocket-powered applications for live dashboards, chat systems, and financial trading platforms with millisecond latency.',
      color: 'linear-gradient(135deg, #f97316, #ea580c)'
    },
    {
      icon: <FaTools />,
      title: 'Legacy Modernization',
      description: 'Migrate outdated systems to modern architectures while preserving business logic and ensuring data integrity.',
      color: 'linear-gradient(135deg, #64748b, #475569)'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }
  };

  return (
    <div 
      style={{
        minHeight: '100vh',
        padding: '80px 20px',
        color: '#fff',
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        backgroundAttachment: 'fixed',
        backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.92), rgba(15, 23, 42, 0.95)), url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        textAlign: 'center'
      }}
      ref={ref}
    >
      <motion.div
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        variants={containerVariants}
        style={{ maxWidth: '1280px', margin: '0 auto' }}
      >
        <motion.h1 
          style={{
                fontSize: 'clamp(2.25rem, 3vw + 1rem, 3.5rem)',
            fontWeight: '700',
            marginBottom: '24px',
            background: 'linear-gradient(to right, #3b82f6, #8b5cf6)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
          variants={itemVariants}
        >
          Web & Software Development
        </motion.h1>

        <motion.p 
          style={{
            fontSize: '1.25rem',
            maxWidth: '800px',
            margin: '0 auto 60px',
            color: '#d1d5db',
            lineHeight: '1.7'
          }}
          variants={itemVariants}
        >
          Comprehensive digital solutions from responsive websites to enterprise-grade applications, all tailored to your business requirements and built with cutting-edge technologies.
        </motion.p>

        {/* Web Development Section */}
        <motion.section 
          variants={containerVariants}
          style={{ marginBottom: '80px' }}
        >
          <motion.h2 
            style={{
              fontSize: '2.5rem',
              fontWeight: '600',
              marginBottom: '40px',
              color: '#e0e7ff'
            }}
            variants={itemVariants}
          >
            Web Development Services
          </motion.h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '30px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {webDevFeatures.map((feature, index) => (
              <motion.div
                key={`web-${index}`}
                style={{
                  background: feature.color,
                  borderRadius: '16px',
                  padding: '30px',
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
                  color: '#fff',
                  textAlign: 'left',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
                variants={itemVariants}
                whileHover={{ 
                  y: -8,
                  scale: 1.03,
                  boxShadow: '0 15px 30px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  marginBottom: '20px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
                }}>
                  {feature.icon}
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  marginBottom: '15px'
                }}>{feature.title}</h3>
                <p style={{
                  fontSize: '1rem',
                  color: '#e5e7eb',
                  lineHeight: '1.6'
                }}>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Custom Software Section */}
        <motion.section variants={containerVariants}>
          <motion.h2 
            style={{
              fontSize: '2.5rem',
              fontWeight: '600',
              marginBottom: '40px',
              color: '#e0e7ff'
            }}
            variants={itemVariants}
          >
            Custom Software Solutions
          </motion.h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '30px',
            maxWidth: '1200px',
            margin: '0 auto'
          }}>
            {customSoftwareFeatures.map((feature, index) => (
              <motion.div
                key={`custom-${index}`}
                style={{
                  background: feature.color,
                  borderRadius: '16px',
                  padding: '30px',
                  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.3)',
                  color: '#fff',
                  textAlign: 'left',
                  transition: 'all 0.3s ease',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}
                variants={itemVariants}
                whileHover={{ 
                  y: -8,
                  scale: 1.03,
                  boxShadow: '0 15px 30px rgba(0, 0, 0, 0.4)'
                }}
              >
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.5rem',
                  marginBottom: '20px',
                  background: 'rgba(255, 255, 255, 0.15)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)'
                }}>
                  {feature.icon}
                </div>
                <h3 style={{
                  fontSize: '1.5rem',
                  fontWeight: '600',
                  marginBottom: '15px'
                }}>{feature.title}</h3>
                <p style={{
                  fontSize: '1rem',
                  color: '#e5e7eb',
                  lineHeight: '1.6'
                }}>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        <ContactForm />
      </motion.div>
    </div>
  );
};

export default CustomSoftwarePage;